import { useState } from "react";
import { FiX } from "react-icons/fi";
import { IBusinessLine } from "@/api/businessLinesApi";
import { billingApi } from "@/api/billingApi";
import { useAvailablePackages } from "@/api/packagesHooks";
import { usePermissions } from "@/contexts/PermissionContext";
import toast from "react-hot-toast";

interface ReservedPackageManagerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessLine: IBusinessLine | null;
}

export default function ReservedPackageManagerDialog({
  isOpen,
  onClose,
  businessLine,
}: ReservedPackageManagerDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const { permissionLevel } = usePermissions();

  // Fetch packages based on line type - only if user is owner to avoid 403 error
  const phoneType =
    businessLine?.line_type === "toll_free" ? "tollfree" : "regular";
  const { data: packagesData, isLoading: isPackagesLoading } =
    useAvailablePackages(phoneType, {
      enabled: permissionLevel === "owner",
    });

  if (!isOpen || !businessLine) return null;

  // Find the matching package for this reserved business line
  const packages = packagesData?.data?.packages || [];
  const packageInfo = packages.find(
    (pkg: any) => pkg.id === businessLine.package_id
  );

  const handlePurchase = async () => {
    if (!businessLine) return;

    setIsLoading(true);
    try {
      // Create subscription for the reserved business line
      const subscriptionResponse = await billingApi.createSubscription(
        businessLine.id
      );

      // Extract subscription ID from the URL in the response
      const url = subscriptionResponse.data.url;
      const urlParams = new URLSearchParams(url.split("?")[1]);
      const subscriptionId = urlParams.get("subscription_id");

      if (subscriptionId) {
        // Create subscription item
        await billingApi.createSubscriptionItem(
          subscriptionId,
          businessLine.id
        );

        toast.success("Business line activated successfully!");
        // Refresh the page to show updated status
        window.location.reload();
      } else {
        toast.error("Invalid subscription response");
      }
    } catch (error: any) {
      console.error("Error activating business line:", error);
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to activate business line. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!businessLine) return;

    if (
      !confirm(
        "Are you sure you want to delete this reserved business line? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      // Dynamic import to maintain consistency with other files
      const { packagesApi } = await import("@/api/packagesApi");
      await packagesApi.deleteReservation();
      toast.success("Reservation deleted successfully!");
      // Close the dialog and refresh the page to update the business lines list
      onClose();
      window.location.reload();
    } catch (error: any) {
      console.error("Error deleting reserved business line:", error);
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to delete reserved business line. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Reserved Business Line
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close dialog"
            aria-label="Close dialog"
            disabled={isLoading}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          {/* Package Information */}
          {permissionLevel === "owner" ? (
            isPackagesLoading ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ) : packageInfo ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Package:</span>
                  <span className="text-sm">{packageInfo.minutes} minutes</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Price:</span>
                  <span className="text-sm font-bold">
                    ${packageInfo.price.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Overage Rate:</span>
                  <span className="text-sm">
                    ${packageInfo.fee_per_overage_minute}/min
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  Package information not available
                </p>
              </div>
            )
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                Package details are only available to account owners. Please
                contact the account owner for pricing information.
              </p>
            </div>
          )}

          <div>
            <p className="text-sm text-gray-600 mb-1">Phone Number</p>
            <p className="font-medium text-gray-900">
              {businessLine.phone_number}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Status</p>
            <span className="inline-block px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-full">
              Reserved
            </span>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Line Type</p>
            <p className="font-medium text-gray-900 capitalize">
              {businessLine.line_type === "toll_free"
                ? "Toll Free"
                : "Local DID"}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Service Type</p>
            <p className="font-medium text-gray-900 capitalize">
              {businessLine.service_type}
            </p>
          </div>

          {businessLine.note?.content && (
            <div>
              <p className="text-sm text-gray-600 mb-1">Note</p>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                {businessLine.note.content}
              </p>
            </div>
          )}

          {businessLine.note?.expires_at && (
            <div>
              <p className="text-sm text-gray-600 mb-1">Expires At</p>
              <p className="text-sm text-red-600 font-medium">
                {new Date(businessLine.note.expires_at).toLocaleDateString()} at{" "}
                {new Date(businessLine.note.expires_at).toLocaleTimeString()}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={handleDelete}
            disabled={isLoading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete Reservation
          </button>
          {permissionLevel === "owner" ? (
            <button
              onClick={handlePurchase}
              disabled={isLoading}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Processing..." : "Activate Line"}
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed"
              title="Only account owners can activate business lines"
            >
              Owner Only
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
